/**
 * SVG sprite registry.
 *
 * Every distinct `.svg` loaded through `<img src>` (or CSS `url()`) gets its own
 * isolated SVG image Document in Chromium/WebKit. This module fetches each SVG
 * once, turns its root into a `<symbol>` inside ONE hidden `<svg id="lobby-svg-sprite">`
 * appended to `document.body`, and hands back the symbol id so callers can render
 * `<svg><use href="#id" /></svg>` in the main document instead.
 *
 * Fetches use `mode: 'cors'` with `credentials: 'same-origin'`: cross-origin they stay
 * anonymous (no cookies, no `Access-Control-Allow-Credentials` needed), so the assets
 * host must send `Access-Control-Allow-Origin` for the lobby origin; same-origin hosts
 * that gate static files on a cookie (Chromatic's capture host) still get it. A non-2xx
 * response, a non-SVG `content-type`, a CORS rejection or a parse failure settles the
 * URL as `null` and the caller keeps its `<img>` — nothing breaks, that icon just does
 * not join the sprite.
 *
 * Symbol content is sanitised (`<script>`, `on*` attributes and `javascript:` hrefs are
 * dropped) and trimmed (`<title>`/`<desc>`/`<metadata>`, comments and whitespace-only
 * text nodes are removed so they do not become tooltips or dead DOM weight).
 *
 * Known limitation: `<style>` blocks inside fetched SVGs become document-global
 * (class selectors are not namespaced; today the portal icons share only three
 * identical rules). Element ids ARE namespaced with the symbol id, including
 * `href="#id"` and `url(#id)` references.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
export const SPRITE_ROOT_ID = 'lobby-svg-sprite';
const SYMBOL_PREFIX = 'svgsprite-';
const ROOT_ATTRS_NOT_COPIED = new Set([
  'id',
  'width',
  'height',
  'viewBox',
  'preserveAspectRatio',
  'version',
  'baseProfile',
  'x',
  'y',
]);
/** SVG's own default when the root sets no preserveAspectRatio. */
const DEFAULT_ASPECT_RATIO = 'xMidYMid meet';
/** Elements that carry no rendering: `<title>` would even surface as a hover tooltip. */
const INERT_ELEMENTS = new Set(['title', 'desc', 'metadata']);
const SVG_CONTENT_TYPE_RE = /svg|xml/i;

export interface SpriteSymbol {
  id: string;
  viewBox: string;
  /**
   * The source root's `preserveAspectRatio` (default `xMidYMid meet`). An `<img>` renders the SVG
   * into its CSS box and lets this attribute fit the viewBox, so the sprite `<svg>` applies it
   * whenever `object-fit` is `fill` (the img default). Jackpot level backgrounds rely on `none`.
   */
  preserveAspectRatio: string;
  /** Intrinsic size from the source's width/height attributes (or viewBox), in px. */
  width?: number;
  height?: number;
}

const settled = new Map<string, SpriteSymbol | null>();
const inflight = new Map<string, Promise<SpriteSymbol | null>>();
let spriteRoot: SVGSVGElement | null = null;

export const hasSpriteSupport = (): boolean =>
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof DOMParser !== 'undefined' &&
  typeof fetch === 'function';

const hashUrl = (url: string): string => {
  let hash = 5381;
  for (let i = 0; i < url.length; i += 1) {
    hash = ((hash << 5) + hash + url.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

const getSpriteRoot = (): SVGSVGElement => {
  if (spriteRoot && spriteRoot.isConnected) return spriteRoot;
  const existing = document.getElementById(SPRITE_ROOT_ID);
  if (existing instanceof SVGSVGElement) {
    spriteRoot = existing;
    return existing;
  }
  const root = document.createElementNS(SVG_NS, 'svg');
  root.id = SPRITE_ROOT_ID;
  root.setAttribute('aria-hidden', 'true');
  root.setAttribute('focusable', 'false');
  root.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden');
  document.body.appendChild(root);
  spriteRoot = root;
  return root;
};

/** Accepts unitless or px lengths only; anything else (%, em, …) is not an intrinsic size. */
const parseLength = (value: string | null): number | undefined => {
  if (!value || !/^\s*\d*\.?\d+(px)?\s*$/.test(value)) return undefined;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const allElements = (root: Element): Element[] => [root, ...Array.from(root.querySelectorAll('*'))];

const sanitize = (root: Element): void => {
  root.querySelectorAll('script').forEach((script) => script.remove());
  allElements(root).forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
      } else if (
        (name === 'href' || name === 'xlink:href') &&
        attr.value.trim().toLowerCase().startsWith('javascript:')
      ) {
        el.removeAttribute(attr.name);
      }
    });
  });
};

/**
 * Drops nodes that never paint: comments, `<title>`/`<desc>`/`<metadata>`, and the
 * whitespace-only text nodes that pretty-printed SVG sources are full of (they made up
 * more than half of the sprite's DOM before this pass). Whitespace inside `<text>` is
 * significant and is left alone.
 */
const stripInertNodes = (root: Element): void => {
  const doomed: Node[] = [];
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT | NodeFilter.SHOW_COMMENT,
  );
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.COMMENT_NODE) {
      doomed.push(node);
    } else if (node.nodeType === Node.TEXT_NODE) {
      if (node.nodeValue?.trim() === '' && !node.parentElement?.closest('text')) doomed.push(node);
    } else if (node instanceof Element && INERT_ELEMENTS.has(node.localName)) {
      doomed.push(node);
    }
    node = walker.nextNode();
  }
  doomed.forEach((n) => n.parentNode?.removeChild(n));
};

const URL_REF_RE = /url\(\s*['"]?#([^'")\s]+)['"]?\s*\)/g;

const namespaceIds = (root: Element, symbolId: string): void => {
  const idElements = Array.from(root.querySelectorAll('[id]'));
  if (idElements.length === 0) return;

  const renames = new Map<string, string>();
  idElements.forEach((el) => {
    const oldId = el.getAttribute('id');
    if (!oldId) return;
    const nextId = `${symbolId}-${oldId}`;
    renames.set(oldId, nextId);
    el.setAttribute('id', nextId);
  });

  const rewriteUrlRefs = (text: string): string =>
    text.replace(URL_REF_RE, (match, name: string) => {
      const next = renames.get(name);
      return next ? `url(#${next})` : match;
    });

  allElements(root).forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name === 'id') return;
      if (name === 'href' || name === 'xlink:href') {
        const target = attr.value.trim();
        if (target.startsWith('#')) {
          const next = renames.get(target.slice(1));
          if (next) el.setAttribute(attr.name, `#${next}`);
        }
      } else if (attr.value.includes('url(')) {
        const next = rewriteUrlRefs(attr.value);
        if (next !== attr.value) el.setAttribute(attr.name, next);
      }
    });
    if (el.tagName.toLowerCase() === 'style' && el.textContent && el.textContent.includes('url(')) {
      el.textContent = rewriteUrlRefs(el.textContent);
    }
  });
};

const buildSymbol = (svgText: string, url: string): SpriteSymbol | null => {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || root.nodeName.toLowerCase() !== 'svg' || doc.querySelector('parsererror')) return null;

  const width = parseLength(root.getAttribute('width'));
  const height = parseLength(root.getAttribute('height'));
  let viewBox = root.getAttribute('viewBox');
  if (!viewBox && width !== undefined && height !== undefined) viewBox = `0 0 ${width} ${height}`;
  if (!viewBox) return null;
  const preserveAspectRatio = root.getAttribute('preserveAspectRatio')?.trim() || DEFAULT_ASPECT_RATIO;

  const id = `${SYMBOL_PREFIX}${hashUrl(url)}`;
  sanitize(root);
  stripInertNodes(root);
  namespaceIds(root, id);

  const symbol = document.createElementNS(SVG_NS, 'symbol');
  symbol.id = id;
  symbol.setAttribute('viewBox', viewBox);
  // Presentation attributes on the source root (fill="none", class, style…) are inherited by its
  // children, so they must travel with the content onto the <symbol>.
  Array.from(root.attributes).forEach((attr) => {
    if (ROOT_ATTRS_NOT_COPIED.has(attr.name) || attr.name.startsWith('xmlns')) return;
    symbol.setAttribute(attr.name, attr.value);
  });
  while (root.firstChild) symbol.appendChild(root.firstChild);

  getSpriteRoot().appendChild(symbol);
  return { id, viewBox, preserveAspectRatio, width, height };
};

const isSvgResponse = (response: Response): boolean => {
  if (!response.ok) return false;
  const contentType = response.headers.get('content-type');
  return contentType === null || SVG_CONTENT_TYPE_RE.test(contentType);
};

/** Synchronous lookup: `undefined` = never requested / still loading, `null` = failed (use `<img>`). */
export const getSpriteSymbol = (url: string): SpriteSymbol | null | undefined => settled.get(url);

export const ensureSpriteSymbol = (url: string): Promise<SpriteSymbol | null> => {
  const done = settled.get(url);
  if (done !== undefined) return Promise.resolve(done);
  const running = inflight.get(url);
  if (running) return running;

  const request = (
    hasSpriteSupport()
      ? // 'same-origin' (the fetch default) rather than 'omit': same-origin hosts that gate static files on a
        // cookie must receive it (Chromatic's capture host answers 401 otherwise, which makes every story with an
        // SVG icon "unstable"). For the cross-origin production CDN it still sends no cookies, so CORS behaviour
        // is unchanged and no `Access-Control-Allow-Credentials` is required.
        fetch(url, { mode: 'cors', credentials: 'same-origin' })
          .then((response) => (isSvgResponse(response) ? response.text() : null))
          .then((text) => (text ? buildSymbol(text, url) : null))
          .catch(() => null)
      : Promise.resolve<SpriteSymbol | null>(null)
  ).then((result) => {
    settled.set(url, result);
    inflight.delete(url);
    return result;
  });

  inflight.set(url, request);
  return request;
};
